import {
  ADMIN_GYM_DECISION_MAX_IDS,
  adminGymDecisionSchema,
  adminGymExerciseGroupSchema,
  adminGymFoodGroupSchema,
  adminGymFoodReportSchema,
  adminGymSearchMissSchema,
  type AdminGymDecision,
  type AdminGymFoodReport,
  type GymSearchKind,
} from '@steado/admin-contract/gym-catalog';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient } from '@/lib/api/client';

/** Every read here shares this prefix, so one invalidation refreshes the screen after a decision. */
export const GYM_CATALOG_KEY = ['admin', 'gym-catalog'] as const;

export function gymFoodGroupsQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: [...GYM_CATALOG_KEY, 'foods'],
    queryFn: () =>
      api.request({ path: '/admin/gym-catalog/foods', schema: z.array(adminGymFoodGroupSchema) }),
  });
}

export function gymExerciseGroupsQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: [...GYM_CATALOG_KEY, 'exercises'],
    queryFn: () =>
      api.request({ path: '/admin/gym-catalog/exercises', schema: z.array(adminGymExerciseGroupSchema) }),
  });
}

export function gymReportsQueryOptions(api: ApiClient, status: AdminGymFoodReport['status']) {
  return queryOptions({
    queryKey: [...GYM_CATALOG_KEY, 'reports', status],
    queryFn: () =>
      api.request({
        path: '/admin/gym-catalog/reports',
        query: { status },
        schema: z.array(adminGymFoodReportSchema),
      }),
  });
}

export function gymSearchMissesQueryOptions(api: ApiClient, kind: GymSearchKind) {
  return queryOptions({
    queryKey: [...GYM_CATALOG_KEY, 'misses', kind],
    queryFn: () =>
      api.request({
        path: '/admin/gym-catalog/search-misses',
        query: { kind },
        schema: z.array(adminGymSearchMissSchema),
      }),
  });
}

/**
 * A decision on a whole group, [ADMIN_GYM_DECISION_MAX_IDS] rows a request:
 * a group holds a row for every gym that added the name, more than one
 * request takes. The server skips a decided row, so after a failure the same
 * call again decides only what is left.
 */
async function inBatches(
  ids: readonly string[],
  decide: (batch: string[]) => Promise<AdminGymDecision>,
): Promise<AdminGymDecision> {
  const total = { decided: 0, pushed: 0 };
  for (let at = 0; at < ids.length; at += ADMIN_GYM_DECISION_MAX_IDS) {
    const result = await decide(ids.slice(at, at + ADMIN_GYM_DECISION_MAX_IDS));
    total.decided += result.decided;
    total.pushed += result.pushed;
  }
  return total;
}

export function linkGymFoods(
  api: ApiClient,
  gymFoodIds: readonly string[],
  catalogFoodId: string,
): Promise<AdminGymDecision> {
  return inBatches(gymFoodIds, (batch) =>
    api.request({
      path: '/admin/gym-catalog/foods/link',
      method: 'POST',
      body: { gymFoodIds: batch, catalogFoodId },
      schema: adminGymDecisionSchema,
    }),
  );
}

export function linkGymExercises(
  api: ApiClient,
  gymExerciseIds: readonly string[],
  catalogExerciseId: string,
): Promise<AdminGymDecision> {
  return inBatches(gymExerciseIds, (batch) =>
    api.request({
      path: '/admin/gym-catalog/exercises/link',
      method: 'POST',
      body: { gymExerciseIds: batch, catalogExerciseId },
      schema: adminGymDecisionSchema,
    }),
  );
}

export function keepGymItems(
  api: ApiClient,
  kind: 'foods' | 'exercises',
  ids: readonly string[],
  note: string,
): Promise<AdminGymDecision> {
  return inBatches(ids, (batch) =>
    api.request({
      path: `/admin/gym-catalog/${kind}/keep`,
      method: 'POST',
      body: { ids: batch, note },
      schema: adminGymDecisionSchema,
    }),
  );
}

export function answerGymReport(
  api: ApiClient,
  id: string,
  verdict: 'resolve' | 'reject',
  resolution: string,
): Promise<AdminGymFoodReport> {
  return api.request({
    path: `/admin/gym-catalog/reports/${id}/${verdict}`,
    method: 'POST',
    body: { resolution },
    schema: adminGymFoodReportSchema,
  });
}

/**
 * The most gym rows "Make a catalog food" carries to the create page, in its
 * URL: past this a group's Make is refused on its face, never cut short.
 * 100 ids are 3,699 characters, which a reload's request line still fits.
 */
export const GYM_ROWS_PER_MAKE = 100;

/** The create page's comma-separated gym ids: up to [GYM_ROWS_PER_MAKE] uuids. */
export const gymIdsSearchSchema = z
  .string()
  .max(GYM_ROWS_PER_MAKE * 37)
  .optional()
  .catch(undefined);

/** `a,b,c` in a URL, as ids; anything that is not a uuid is dropped. */
export function idList(value: string | undefined): string[] {
  if (value === undefined) return [];
  return value.split(',').filter((id) => /^[0-9a-f-]{36}$/i.test(id));
}
